import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [error, setError] = useState("");

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /auth/me check.
    // AuthCallback will exchange the session_id first and then call setSession().
    // Without this guard, /auth/me fires with no token → user is briefly set to
    // `false`, then AuthCallback flips it back — causing UI flicker/race.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    const token = localStorage.getItem("ba_token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("ba_token");
        setUser(false);
      });
  }, []);

  const login = useCallback(async (identifier, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      localStorage.setItem("ba_token", data.token);
      // Merge guest cart into user cart (best-effort — never blocks login)
      const sid = localStorage.getItem("ba_shop_sid");
      if (sid) {
        try {
          await api.post("/shop/cart/merge", { session_id: sid }, {
            headers: { Authorization: `Bearer ${data.token}` },
          });
          localStorage.removeItem("ba_shop_sid");
        } catch (_) { /* ignore */ }
      }
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("ba_token");
    setUser(false);
  }, []);

  const setSession = useCallback((token, u) => {
    localStorage.setItem("ba_token", token);
    setUser(u);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, setSession, error }),
    [user, login, logout, setSession, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
